import {
  clearPortalSession,
  notifyPortalSessionExpired,
  persistPortalSession,
} from '@/lib/auth/portal-session'
import {
  getStoredRefreshToken,
  getStoredSessionSource,
  getStoredToken,
  type PortalKind,
} from '@/lib/auth/token-storage'

interface RefreshSuccessResponse {
  code: 'success'
  data: {
    password_reset_required?: boolean
    refresh_token?: string | null
    token: string
    user: unknown
  }
}

const refreshEndpointPath: Record<PortalKind, string> = {
  admin: '/admin/auth/refresh',
  user: '/app/auth/refresh',
}
const userSsoRefreshEndpointPath = '/app/auth/sso/refresh'

// 同一浏览器标签页内的并发 401 只触发一次 refresh 请求。
const refreshRequests = new Map<PortalKind, Promise<boolean>>()
const refreshLockMaxAgeMs = 15_000
const refreshWaitTimeoutMs = 10_000
const refreshWaitPollMs = 150
// localStorage lock 用于跨标签页协作；它是 best-effort 锁，所以必须有过期时间。
const refreshLockKey: Record<PortalKind, string> = {
  admin: 'aihub:admin:refresh-lock',
  user: 'aihub:user:refresh-lock',
}
const legacyRefreshLockKey: Record<PortalKind, string> = {
  admin: 'aiportal:admin:refresh-lock',
  user: 'aiportal:user:refresh-lock',
}

interface RefreshLock {
  owner: string
  startedAt: number
}

/**
 * 判断请求路径是否为 refresh endpoint。
 * 401 重试逻辑必须排除 refresh 自身，否则会造成递归刷新。
 */
export function isRefreshEndpointPath(path: string) {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  return (
    Object.values(refreshEndpointPath).includes(normalizedPath) ||
    normalizedPath === userSsoRefreshEndpointPath
  )
}

/**
 * 刷新门户 session。
 * 返回 `true` 表示本标签页或其他标签页已经写入新 token；返回 `false` 表示 session 不可恢复。
 */
export async function refreshPortalSession(
  portal: PortalKind,
  resolveUrl: (path: string) => string,
): Promise<boolean> {
  const activeRefresh = refreshRequests.get(portal)
  if (activeRefresh) {
    return activeRefresh
  }

  const refreshRequest = refreshPortalSessionOnce(portal, resolveUrl).finally(() => {
    refreshRequests.delete(portal)
  })

  refreshRequests.set(portal, refreshRequest)
  return refreshRequest
}

async function refreshPortalSessionOnce(portal: PortalKind, resolveUrl: (path: string) => string) {
  const refreshToken = getStoredRefreshToken(portal)
  if (!refreshToken) {
    return failRefresh(portal)
  }

  // 如果其他标签页正在刷新，先等待它写入新 token，减少 refresh token 被并发消费的概率。
  if (await waitForExternalRefresh(portal, refreshToken)) {
    return true
  }

  const owner = createRefreshLockOwner()
  acquireRefreshLock(portal, owner)

  if (!isRefreshLockOwner(portal, owner) && (await waitForExternalRefresh(portal, refreshToken))) {
    return true
  }

  try {
    const sessionSource = getStoredSessionSource(portal)
    const endpoint =
      portal === 'user' && sessionSource === 'sso'
        ? userSsoRefreshEndpointPath
        : refreshEndpointPath[portal]
    const response = await fetch(resolveUrl(endpoint), {
      body: JSON.stringify({ refresh_token: refreshToken }),
      credentials: portal === 'user' ? 'include' : undefined,
      headers: {
        'content-type': 'application/json',
      },
      method: 'POST',
    }).catch(() => undefined)

    if (!response?.ok) {
      return recoverFromExternalRefreshOrFail(portal, refreshToken)
    }

    const body = (await response.json().catch(() => undefined)) as
      | Partial<RefreshSuccessResponse>
      | undefined
    // refresh 成功必须同时轮换 access token 和 refresh token；缺任意一个都按不可恢复处理。
    if (body?.code !== 'success' || !body.data?.token || !body.data.refresh_token) {
      return recoverFromExternalRefreshOrFail(portal, refreshToken)
    }

    persistPortalSession(portal, body.data, { source: sessionSource })
    return true
  } finally {
    releaseRefreshLock(portal, owner)
  }
}

function failRefresh(portal: PortalKind) {
  clearPortalSession(portal)
  notifyPortalSessionExpired(portal)
  return false
}

function createRefreshLockOwner() {
  return `${Date.now().toString(36)}-${Math.random().toString(16).slice(2)}`
}

function readRefreshLock(portal: PortalKind): RefreshLock | null {
  const rawValue =
    window.localStorage.getItem(refreshLockKey[portal]) ??
    window.localStorage.getItem(legacyRefreshLockKey[portal])
  if (!rawValue) {
    return null
  }

  try {
    const parsed = JSON.parse(rawValue) as Partial<RefreshLock>
    if (typeof parsed.owner === 'string' && typeof parsed.startedAt === 'number') {
      return {
        owner: parsed.owner,
        startedAt: parsed.startedAt,
      }
    }
  } catch {
    return null
  }

  return null
}

function isRefreshLockActive(lock: RefreshLock | null) {
  return Boolean(lock && Date.now() - lock.startedAt <= refreshLockMaxAgeMs)
}

function acquireRefreshLock(portal: PortalKind, owner: string) {
  window.localStorage.setItem(
    refreshLockKey[portal],
    JSON.stringify({
      owner,
      startedAt: Date.now(),
    } satisfies RefreshLock),
  )
}

function isRefreshLockOwner(portal: PortalKind, owner: string) {
  return readRefreshLock(portal)?.owner === owner
}

function releaseRefreshLock(portal: PortalKind, owner: string) {
  if (isRefreshLockOwner(portal, owner)) {
    window.localStorage.removeItem(refreshLockKey[portal])
  }
}

function sleep(milliseconds: number) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, milliseconds)
  })
}

async function waitForExternalRefresh(portal: PortalKind, originalRefreshToken: string) {
  const lock = readRefreshLock(portal)
  if (!isRefreshLockActive(lock)) {
    return false
  }

  const deadline = Date.now() + refreshWaitTimeoutMs
  while (Date.now() < deadline) {
    if (hasExternalRefreshSucceeded(portal, originalRefreshToken)) {
      return true
    }

    if (!isRefreshLockActive(readRefreshLock(portal))) {
      return hasExternalRefreshSucceeded(portal, originalRefreshToken)
    }

    await sleep(refreshWaitPollMs)
  }

  return hasExternalRefreshSucceeded(portal, originalRefreshToken)
}

function hasExternalRefreshSucceeded(portal: PortalKind, originalRefreshToken: string) {
  const nextRefreshToken = getStoredRefreshToken(portal)
  return Boolean(
    getStoredToken(portal) && nextRefreshToken && nextRefreshToken !== originalRefreshToken,
  )
}

/**
 * 网络失败或响应异常时，再检查一次是否有其他标签页已经完成 refresh。
 * 这样可以避免“一个标签页成功、另一个标签页失败”导致用户被错误登出。
 */
function recoverFromExternalRefreshOrFail(portal: PortalKind, originalRefreshToken: string) {
  if (hasExternalRefreshSucceeded(portal, originalRefreshToken)) {
    return true
  }

  return failRefresh(portal)
}
